import {type ReactNode} from 'react';
import {cn} from '@/matic/lib/utils/cn';

type SlideSwapProps = {
  /**
   * When false the `primary` panel is shown; when true `secondary` slides
   * in over it (and `primary` slides out). Toggling this is what drives the
   * cross-transition.
   */
  showSecondary: boolean;
  primary: ReactNode;
  secondary: ReactNode;
  /**
   * Applied to the positioning container. Give it the size you want the
   * swap to occupy — e.g. `flex-1` inside a flex column.
   */
  className?: string;
};

/**
 * Cross-transitions between two stacked panels. Both panels stay mounted and
 * are absolutely positioned over each other; flipping `showSecondary` slides
 * one out (toward the left) while the other slides in (from the right), with
 * a fade. Because both elements are always mounted, the swap is a real CSS
 * transition rather than an instant unmount/mount — the outgoing panel is
 * still on screen to animate against the incoming one.
 *
 * Each panel layer is a full-height flex column, so a child that uses
 * `flex-1` (e.g. a scrollable `<nav>`/list) fills the available space as it
 * would outside the swap.
 *
 * Reuse anywhere you toggle between exactly two stacked views — normal nav vs
 * a sub-nav, read view vs edit, list vs filter panel, etc.
 */
export function SlideSwap({
  showSecondary,
  primary,
  secondary,
  className,
}: SlideSwapProps) {
  const layerBase =
    'absolute inset-0 flex flex-col transition-all duration-200 ease-out motion-reduce:transition-none';

  return (
    <div className={cn('relative overflow-hidden', className)}>
      <div
        aria-hidden={showSecondary}
        className={cn(
          layerBase,
          showSecondary
            ? 'pointer-events-none -translate-x-4 opacity-0'
            : 'translate-x-0 opacity-100'
        )}
      >
        {primary}
      </div>
      <div
        aria-hidden={!showSecondary}
        className={cn(
          layerBase,
          showSecondary
            ? 'translate-x-0 opacity-100'
            : 'pointer-events-none translate-x-4 opacity-0'
        )}
      >
        {secondary}
      </div>
    </div>
  );
}
